import { analytics } from '@sendgrid/bloodhound';
import PROFILE from '../constants/profile';
import {
  ACTIONS,
  ADDONS,
  COOKIES,
  ERRORTYPES,
  APPINDICATORS,
  ENVIRONMENT,
  LOCALSTORAGE,
  TELEMETRY_ERROR_VALUES,
} from '../constants';
import {
  validateName,
  validPhoneNumber,
  validatePhoneNumber,
  validateWebsite,
  validateCompanyName,
  trimCompanyName,
  trimWebsite,
} from '../utils/profileInput';
import { validateEmail } from '../utils/formInput';
import createRequest from '../utils/requestService';
import getEnvVariable from '../utils/environment';
import { setCookie, removeCookie, getCookie } from '../utils/cookieHelpers';
import accountTypes from '../constants/accountTypes';
import { trackOnce } from '../utils/Analytics';
import { telemetry } from '../utils/telemetry';
import { AccountDetailsEvents } from '../analytics/accountDetailsEvents';

const getEmailVolumeValue = (emailVolume) => {
  switch (emailVolume) {
    case PROFILE.LV_EMAIL:
      return PROFILE.LV_EMAIL_VAL;
    case PROFILE.MLV_EMAIL:
      return PROFILE.MLV_EMAIL_VAL;
    case PROFILE.MV_EMAIL:
      return PROFILE.MV_EMAIL_VAL;
    case PROFILE.MHV_EMAIL:
      return PROFILE.MHV_EMAIL_VAL;
    case PROFILE.HLV_EMAIL:
      return PROFILE.HLV_EMAIL_VAL;
    case PROFILE.HMV_EMAIL:
      return PROFILE.HMV_EMAIL_VAL;
    case PROFILE.HV_EMAIL:
      return PROFILE.HV_EMAIL_VAL;
    default:
      return ERRORTYPES.NO_TYPE_SET;
  }
};

const hasIP = addOns => addOns && addOns[ADDONS.DEDICATED_IP] > 0;

const hasEase = addOns => addOns && addOns[ADDONS.EASE] > 3;

const cleanUpSignUpSession = () => {
  removeCookie(COOKIES.SIGNUP_SESSION);
  localStorage.removeItem(LOCALSTORAGE.REDUX_STATE);
};

export const setEhawkTalon = talon => ({
  type: ACTIONS.SET_EHAWK_TALON,
  talon,
});

const trackFirstNameCompleted = trackOnce();
export const setFirstName = firstName => ({
  type: ACTIONS.SET_FIRST_NAME,
  firstName,
});
export const blurFirstName = firstName => () => {
  const firstNameError = validateName(firstName);
  if (!firstNameError) {
    trackFirstNameCompleted(AccountDetailsEvents.firstNameCompleted());
  }
};

const trackLastNameCompleted = trackOnce();
export const setLastName = lastName => ({
  type: ACTIONS.SET_LAST_NAME,
  lastName,
});
export const blurLastName = lastName => () => {
  const lastNameError = validateName(lastName);
  if (!lastNameError) {
    trackLastNameCompleted(AccountDetailsEvents.lastNameCompleted());
  }
};

const trackPhoneNumberCompleted = trackOnce();
export const setPhoneNumber = phoneNumber => ({
  type: ACTIONS.SET_PHONE_NUMBER,
  phoneNumber,
});
export const blurPhoneNumber = phoneNumber => () => {
  const phoneNumberVal = validPhoneNumber(phoneNumber);
  const phoneNumberError = validatePhoneNumber(phoneNumberVal);
  if (!phoneNumberError) {
    trackPhoneNumberCompleted(AccountDetailsEvents.phoneNumberCompleted());
  }
};

const trackCompanyCompleted = trackOnce();
export const setCompanyName = companyName => ({
  type: ACTIONS.SET_COMPANY_NAME,
  companyName,
});
export const blurCompanyName = companyName => () => {
  const companyNameVal = trimCompanyName(companyName);
  const companyNameError = validateCompanyName(companyNameVal);
  if (!companyNameError) {
    trackCompanyCompleted(AccountDetailsEvents.companyNameCompleted());
  }
};

const trackWebsiteUrlCompleted = trackOnce();
export const setWebsite = website => ({ type: ACTIONS.SET_WEBSITE, website });
export const blurWebsite = website => () => {
  const websiteVal = trimWebsite(website);
  const websiteError = validateWebsite(websiteVal);
  if (!websiteError) {
    trackWebsiteUrlCompleted(AccountDetailsEvents.companyWebsiteCompleted());
  }
};

const trackEmailComplete = trackOnce();
export const setEmail = email => ({ type: ACTIONS.SET_EMAIL, email });
export const blurEmail = email => () => {
  const emailError = validateEmail(email).errorType;
  if (emailError === ERRORTYPES.NO_TYPE_SET) {
    trackEmailComplete('Profile email complete');
  }
};

const trackProfileRoleSelected = trackOnce();
export const setOccupation = (occupation) => {
  // using the word "role" here because of legacy naming
  trackProfileRoleSelected(AccountDetailsEvents.yourRoleCompleted(occupation));
  return { type: ACTIONS.SET_OCCUPATION, occupation };
};

export const toggleIsTransactional = () => ({ type: ACTIONS.TOGGLE_IS_TRANSACTIONAL });

export const toggleIsMarketing = () => ({ type: ACTIONS.TOGGLE_IS_MARKETING });

const trackEmailVolumeSelected = trackOnce();
export const setEmailVolume = (emailVolume) => {
  trackEmailVolumeSelected(AccountDetailsEvents.emailVolumeCompleted(emailVolume));
  return { type: ACTIONS.SET_EMAIL_VOLUME, emailVolume };
};

const trackEmployeeCountSelected = trackOnce();
export const setEmployeeCount = (employeeCount) => {
  trackEmployeeCountSelected(AccountDetailsEvents.employeeCountCompleted(employeeCount));
  return {
    type: ACTIONS.SET_EMPLOYEE_COUNT,
    employeeCount,
  };
};

export const setCountryCode = countryCode => ({
  type: ACTIONS.SET_COUNTRY_CODE,
  countryCode,
});

export const checkIsFormValid = () => ({ type: ACTIONS.CHECK_IS_FORM_VALID });

export const fetchStatus = () => dispatch => fetch(createRequest('/v3/s2s/signup/status', { method: 'GET' }))
  .then((response) => {
    if (response.ok) {
      response.json().then((json) => {
        dispatch({
          type: ACTIONS.USER_STATUS_FETCHED,
          status: json,
        });
      });
    }
  });

const addEase = () => fetch(createRequest('/v3/billing/message_storage'));

const saveResponse = async ({
  response,
  addOns,
  history,
  dispatch,
  profileInputs,
}) => {
  const isAds = window.location.href.indexOf(accountTypes.ads) > -1;
  if (response.ok) {
    analytics.track(AccountDetailsEvents.accountProvisioned());

    // track event in Optimizely
    window.optimizely = window.optimizely || [];
    window.optimizely.push({
      type: 'event',
      eventName: 'Profile page complete',
    });

    const formattedOccupation = profileInputs.occupation.toLowerCase();
    // create a cookie for Mako to read to promote services to specific user segments with AppCues
    setCookie(COOKIES.USER_OCCUPATION, formattedOccupation);

    if (hasIP(addOns)) {
      const ipAddon = addOns[ADDONS.DEDICATED_IP];
      analytics.track(AccountDetailsEvents.ipAdded(ipAddon));
    }
    if (hasEase(addOns)) {
      const easeReq = await addEase();
      if (easeReq.ok) {
        const easeAddon = addOns[ADDONS.EASE];
        analytics.track(AccountDetailsEvents.easeAdded(easeAddon));
      }
    }

    cleanUpSignUpSession();

    if (isAds) {
      window.location.replace(`${getEnvVariable(ENVIRONMENT.LABS_URL)}/grow`);
    } else {
      window.location.replace(`${getEnvVariable(ENVIRONMENT.MAKO_HOST_URL)}/guide?from=profile&integrate=true`);
    }
  } else if (response.status === 403) {
    removeCookie(COOKIES.MAKO_AUTH);
    cleanUpSignUpSession();
    analytics.track(AccountDetailsEvents.accountBanned());
    history.push('/declined');
  } else {
    const {
      ERROR_MESSAGES, TAGS, TAG_VALUES, LEVELS,
    } = TELEMETRY_ERROR_VALUES;
    dispatch({ type: ACTIONS.USER_PROFILE_FAILED });
    dispatch({
      type: APPINDICATORS.DISPLAY_ALERT,
      alertType: APPINDICATORS.ALERT_TYPES.DANGER,
      alertText: APPINDICATORS.ALERT_TEXT.GENERIC_ERROR,
    });
    response.json()
      .then((data) => {
        telemetry.captureError(
          data.errors[0].message,
          TAGS.PROVISION,
          TAG_VALUES.PROVISION_FAILURE,
          LEVELS.FATAL,
        );
      })
      .catch(() => {
        telemetry.captureError(
          ERROR_MESSAGES.NO_MESSAGE,
          TAGS.PROVISION,
          TAG_VALUES.PROVISION_FAILURE,
          LEVELS.FATAL,
        );
      });
  }
  dispatch({ type: APPINDICATORS.PAGE_LOADING_FINISHED });
};

export const onSave = (profileInputs, history) => (dispatch, getState) => {
  const { addOns } = getState();
  dispatch({ type: APPINDICATORS.PAGE_LOADING });
  const phoneNumber = profileInputs.phoneNumber !== '' ? `+${profileInputs.countryCode}${profileInputs.phoneNumber}` : '';
  const isAds = window.location.href.indexOf(accountTypes.ads) > -1;
  const userProfile = {
    first_name: profileInputs.firstName,
    last_name: profileInputs.lastName,
    phone: phoneNumber,
    company: profileInputs.companyName,
    website: profileInputs.website,
  };

  const payload = {
    talon: profileInputs.talon.value,
    occupation: profileInputs.occupation,
    employee_count: profileInputs.employeeCount,
    is_transactional: profileInputs.isTransactional || 0,
    is_marketing: profileInputs.isMarketing || 0,
    email_volume: profileInputs.emailVolume
      ? getEmailVolumeValue(profileInputs.emailVolume)
      : PROFILE.LV_EMAIL_VAL,
    user_profile: userProfile,
  };

  // the backend requires the employee count and occupation fields, but that ads sign-up does not
  // if the account is ads we populate it with default values
  if (isAds) {
    payload.employee_count = PROFILE.LV_EMPLOYEE;
    payload.occupation = PROFILE.OTHER;
  }

  // it's a reseller!
  if (profileInputs.email) {
    payload.email = profileInputs.email;
  }

  const token = getCookie(COOKIES.MAKO_AUTH);

  const options = {
    method: 'POST',
    headers: {
      Authorization: `token ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ...payload }),
  };

  const result = fetch(
    `${getEnvVariable(ENVIRONMENT.MAKO_URL)}/v3/s2s/signup/provision`,
    options,
  ).then((response) => {
    const saveProvisionInfo = {
      response,
      addOns,
      history,
      dispatch,
      profileInputs,
    };
    saveResponse(saveProvisionInfo);
  });

  return result;
};

export const setAdsUser = isAdsUser => ({
  type: ACTIONS.SET_ADS_USER,
  isAdsUser,
});
